import { useState, useRef } from "react";

export interface UseFormHandlerReturn {
  email: string;
  name: string;
  affiliation: string;
  message: string;
  isValidEmail: boolean | null;
  emailError: string;
  showAccessModal: boolean;
  showThanksModal: boolean;
  isSubmitting: boolean;
  formRef: React.RefObject<HTMLFormElement>;
  setEmailHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setNameHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setAffiliationHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setMessageHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formSubmitHandler: (e: React.FormEvent<HTMLFormElement>) => void;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setName: React.Dispatch<React.SetStateAction<string>>;
  setAffiliation: React.Dispatch<React.SetStateAction<string>>;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  setIsValidEmail: React.Dispatch<React.SetStateAction<boolean | null>>;
  setEmailError: React.Dispatch<React.SetStateAction<string>>;
  setShowAccessModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowThanksModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const useFormHandler = (): UseFormHandlerReturn => {
  // This is for the script running in preview@ account.
  const scriptURL =
    "https://script.google.com/macros/s/AKfycbwEqVL6ttm1te7ofrAiCYJ22TbotBeCIdKZqkIrd-TY4x5vmeZUaZBfA2F_sAAtA7Xi/exec";

  const [email, setEmail] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [affiliation, setAffiliation] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isValidEmail, setIsValidEmail] = useState<boolean | null>(null);
  const [emailError, setEmailError] = useState<string>("");
  const [showAccessModal, setShowAccessModal] = useState<boolean>(false);
  const [showThanksModal, setShowThanksModal] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  const setEmailHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (!checkIsValidEmail(value)) {
      setIsValidEmail(false);
      setEmailError("*invalid email address");
    } else {
      setIsValidEmail(true);
      setEmailError("");
    }
  };

  const setNameHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
  };

  const setAffiliationHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAffiliation(value);
  };

  const setMessageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessage(value);
  };

  const formSubmitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    let requestBody = new FormData(formRef.current!);

    fetch(scriptURL, { method: "POST", body: requestBody, mode: "no-cors" })
      .then(() => {
        setIsSubmitting(false);
        setEmail("");
        setShowAccessModal(false);
        setShowThanksModal(true);
      })
      .catch(() => {
        setIsSubmitting(false);
        setIsValidEmail(false);
        setEmailError("*invalid email address");
      });
  };

  function checkIsValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  return {
    email,
    name,
    affiliation,
    message,
    isValidEmail,
    emailError,
    showAccessModal,
    showThanksModal,
    isSubmitting,
    formRef,
    setEmailHandler,
    setNameHandler,
    setAffiliationHandler,
    setMessageHandler,
    formSubmitHandler,
    setEmail,
    setName,
    setAffiliation,
    setMessage,
    setIsValidEmail,
    setEmailError,
    setShowAccessModal,
    setShowThanksModal,
  };
};

export default useFormHandler;
